import {
  Box,
  Typography,
  TextField,
  Switch,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useUserProfileInfosMutation, useUpdateUserMutation } from "state/api";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  VehicleSkeleton,
  VehicleFormSkeleton,
} from "components/LoadingSkeletons";

const fuelOptions = ["DIESEL", "ESSENCE", "ESS.GPL"];

const textFieldStyles = {
  "& label.Mui-focused": { color: "#0085FF" },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#0085FF" },
    "&.Mui-focused fieldset": { borderColor: "#0085FF" },
  },
  "& .MuiInputBase-root": {
    backgroundColor: "#FAFBFC",
  },
};

const slideIn = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
};

const Vehicule = () => {
  const userId = useSelector((state) => state.global.userId);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [vehicleReminder, setVehicleReminder] = useState(false);
  const [hasVehicleInfo, setHasVehicleInfo] = useState(null);

  // RTK Queries
  const [
    fetchUserProfile,
    { data: profileData, error: profileError, isLoading: isProfileLoading },
  ] = useUserProfileInfosMutation();
  const [updateUser, { isLoading: updateIsLoading }] = useUpdateUserMutation();

  const [vehiculeInfo, setVehiculeInfo] = useState({
    marque: "",
    model: "",
    year: "",
    fuelType: "",
    insuranceValidity: "",
    technicalControlValidity: "",
  });
  const [editField, setEditField] = useState(null); // Track the field being edited

  // Fetch Profile Data
  useEffect(() => {
    const fetchProfileData = async () => {
      if (userId) {
        try {
          const response = await fetchUserProfile({ userId }).unwrap();
          console.log(response);

          const vehicleData = response?.vehicule || {};
          setVehiculeInfo(vehicleData);
          setVehicleReminder(
            response?.userReceiveVehiculeNotifications || false
          );

          // Check if user has vehicle info (any field filled)
          const hasInfo = Object.values(vehicleData).some(
            (value) => value && value !== ""
          );
          setHasVehicleInfo(hasInfo);
        } catch (error) {
          console.error("Error fetching profile info:", error);
          setHasVehicleInfo(false);
        }
      }
    };
    fetchProfileData();
  }, [userId, fetchUserProfile]);
  const handleSubmitVehicleForm = async (e) => {
    e.preventDefault();
    try {
      await updateUser({
        userId,
        updates: {
          vehicleReminder: true,
          vehicleInfo: {
            marque: vehiculeInfo.marque,
            model: vehiculeInfo.model,
            year: vehiculeInfo.year,
            fuelType: vehiculeInfo.fuelType,
            insuranceValidity: vehiculeInfo.insuranceValidity,
            technicalControlValidity: vehiculeInfo.technicalControlValidity,
          },
        },
      }).unwrap();
      setHasVehicleInfo(true);
      setVehicleReminder(true);
    } catch (err) {
      console.error("Error submitting vehicle info", err);
      alert("Erreur lors de l'enregistrement !");
    }
  };
  // Handle Edit Button Click
  const handleEdit = (field) => {
    setEditField(field);
  };

  // Handle Input Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVehiculeInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVehicleReminderChange = async (event) => {
    const newValue = event.target.checked;
    setVehicleReminder(newValue); // Update UI immediately

    try {
      await updateUser({
        userId,
        updates: { vehicleReminder: newValue },
      }).unwrap();
    } catch (error) {
      console.error("Error updating vehicle reminder:", error);
      alert("Erreur lors de la mise à jour !");
    }
  };

  // Handle Date Change
  const handleDateChange = (field, newValue) => {
    setVehiculeInfo((prev) => ({
      ...prev,
      [field]: newValue ? newValue.toISOString() : "",
    }));
  };

  // Handle Save
  const handleSave = async (field) => {
    try {
      await updateUser({
        userId,
        updates: { [`vehicleInfo.${field}`]: vehiculeInfo[field] },
      }).unwrap();
      setEditField(null);
    } catch (error) {
      console.error("Error updating vehicle info:", error);
      alert("Erreur lors de la mise à jour !");
    }
  };

  if (isProfileLoading) {
    return <VehicleSkeleton />;
  }

  if (profileError) {
    return (
      <Typography>
        Error loading vehicle information: {profileError.message}
      </Typography>
    );
  }

  if (hasVehicleInfo === null) {
    return <VehicleSkeleton />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          width: "50rem",
          margin: "auto",
          minHeight: "50vh",
          padding: "30px",
          borderRadius: "12px",
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
          backgroundColor: "#FFFFFF",
          fontFamily: "Arial, sans-serif",
          border: "1px solid #E8F4FD",
        }}
      >
        {!hasVehicleInfo ? (
          <motion.div
            key="form"
            variants={slideIn}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: 30,
                fontFamily: "raspoutine",
                fontWeight: "bold",
                color: "#0085FF",
                marginBottom: "10px",
                whiteSpace: "nowrap",
                wordSpacing: "8px",
              }}
            >
              Souscrire à la rubrique
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: 30,
                fontFamily: "raspoutine",
                fontWeight: "bold",
                color: "#0085FF",
                marginBottom: "10px",
              }}
            >
              Mon Véhicule
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmitVehicleForm}
              sx={{
                textAlign: "left",
                maxHeight: isMobile ? "70vh" : "60vh",
                overflowY: "auto",
                pr: 1,
              }}
            >
              <TextField
                label="Marque du véhicule"
                fullWidth
                margin="normal"
                name="marque"
                value={vehiculeInfo.marque}
                onChange={handleInputChange}
                sx={textFieldStyles}
              />
              <TextField
                label="Modèle du véhicule"
                fullWidth
                margin="normal"
                name="model"
                value={vehiculeInfo.model}
                onChange={handleInputChange}
                sx={textFieldStyles}
              />

              <TextField
                label="Année du véhicule"
                fullWidth
                margin="normal"
                name="year"
                value={vehiculeInfo.year}
                onChange={handleInputChange}
                inputProps={{ maxLength: 4, pattern: "\\d{4}" }}
                placeholder="YYYY"
                sx={textFieldStyles}
              />

              <FormControl component="fieldset" fullWidth margin="normal">
                <FormLabel
                  component="legend"
                  sx={{
                    color: "#006bbf",
                    "&.Mui-focused": { color: "#006bbf" },
                  }}
                >
                  Type de carburant
                </FormLabel>
                <RadioGroup
                  row
                  name="fuelType"
                  value={vehiculeInfo.fuelType}
                  onChange={handleInputChange}
                  sx={{ gap: isMobile ? ".5em" : "1.5em" }}
                >
                  {fuelOptions.map((option) => (
                    <FormControlLabel
                      key={option}
                      value={option}
                      control={
                        <Radio
                          sx={{
                            color: "#006bbf",
                            "&.Mui-checked": { color: "#006bbf" },
                          }}
                        />
                      }
                      label={option}
                      sx={{ color: "#006bbf" }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              <TextField
                label="Validité de l'assurance"
                type="date"
                fullWidth
                margin="normal"
                name="insuranceValidity"
                value={vehiculeInfo.insuranceValidity}
                InputLabelProps={{ shrink: true }}
                onChange={handleInputChange}
                sx={textFieldStyles}
              />

              <TextField
                label="Validité du contrôle technique"
                type="date"
                fullWidth
                margin="normal"
                name="technicalControlValidity"
                value={vehiculeInfo.technicalControlValidity}
                InputLabelProps={{ shrink: true }}
                onChange={handleInputChange}
                sx={textFieldStyles}
              />

              <Button
                type="submit"
                variant="contained"
                sx={{
                  mt: 3,
                  py: 1.5,
                  px: 4,
                  backgroundColor: "#0085FF",
                  color: "white",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  textTransform: "none",
                  boxShadow: "0 4px 12px rgba(0, 133, 255, 0.3)",
                  "&:hover": {
                    backgroundColor: "#006bbf",
                    boxShadow: "0 6px 16px rgba(0, 133, 255, 0.4)",
                  },
                }}
              >
                Enregistrer mes informations véhicule
              </Button>
            </Box>
          </motion.div>
        ) : (
          <>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "24px",
                marginBottom: "20px",
                color: "#0085FF",
                borderBottom: "2px solid #0085FF",
                paddingBottom: "8px",
              }}
            >
              Informations du Véhicule
            </Typography>

            {/* Marque */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
                padding: "12px",
                borderRadius: "8px",
                backgroundColor: "#F8FAFC",
                border: "1px solid #E5E7EB",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                  borderColor: "#CBD5E1",
                },
              }}
            >
              {editField === "marque" ? (
                <TextField
                  fullWidth
                  name="marque"
                  value={vehiculeInfo.marque}
                  onChange={handleInputChange}
                  sx={textFieldStyles}
                />
              ) : (
                <Typography
                  sx={{
                    fontWeight: "600",
                    fontSize: "14px",
                    flex: 1,
                    color: "#374151",
                  }}
                >
                  Marque:{" "}
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: "normal",
                      color: "#6B7280",
                    }}
                  >
                    {vehiculeInfo.marque || "N/A"}
                  </Typography>
                </Typography>
              )}
              <IconButton
                onClick={() =>
                  editField === "marque"
                    ? handleSave("marque")
                    : handleEdit("marque")
                }
                sx={{
                  color: "#0085FF",
                  "&:hover": {
                    backgroundColor: "#E3F2FD",
                  },
                }}
              >
                {editField === "marque" ? <SaveIcon /> : <EditIcon />}
              </IconButton>
            </Box>

            {/* Modèle */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
                padding: "12px",
                borderRadius: "8px",
                backgroundColor: "#F8FAFC",
                border: "1px solid #E5E7EB",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                  borderColor: "#CBD5E1",
                },
              }}
            >
              {editField === "model" ? (
                <TextField
                  fullWidth
                  name="model"
                  value={vehiculeInfo.model}
                  onChange={handleInputChange}
                  sx={textFieldStyles}
                />
              ) : (
                <Typography
                  sx={{
                    fontWeight: "600",
                    fontSize: "14px",
                    flex: 1,
                    color: "#374151",
                  }}
                >
                  Modèle:{" "}
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: "normal",
                      color: "#6B7280",
                    }}
                  >
                    {vehiculeInfo.model || "N/A"}
                  </Typography>
                </Typography>
              )}
              <IconButton
                onClick={() =>
                  editField === "model"
                    ? handleSave("model")
                    : handleEdit("model")
                }
                sx={{
                  color: "#0085FF",
                  "&:hover": {
                    backgroundColor: "#E3F2FD",
                  },
                }}
              >
                {editField === "model" ? <SaveIcon /> : <EditIcon />}
              </IconButton>
            </Box>

            {/* Année */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
                padding: "12px",
                borderRadius: "8px",
                backgroundColor: "#F8FAFC",
                border: "1px solid #E5E7EB",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                  borderColor: "#CBD5E1",
                },
              }}
            >
              {editField === "year" ? (
                <TextField
                  fullWidth
                  name="year"
                  type="number"
                  value={vehiculeInfo.year}
                  onChange={handleInputChange}
                  sx={textFieldStyles}
                />
              ) : (
                <Typography
                  sx={{
                    fontWeight: "600",
                    fontSize: "14px",
                    flex: 1,
                    color: "#374151",
                  }}
                >
                  Année:{" "}
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: "normal",
                      color: "#6B7280",
                    }}
                  >
                    {vehiculeInfo.year || "N/A"}
                  </Typography>
                </Typography>
              )}
              <IconButton
                onClick={() =>
                  editField === "year" ? handleSave("year") : handleEdit("year")
                }
                sx={{
                  color: "#0085FF",
                  "&:hover": {
                    backgroundColor: "#E3F2FD",
                  },
                }}
              >
                {editField === "year" ? <SaveIcon /> : <EditIcon />}
              </IconButton>
            </Box>

            {/* Type de carburant (Dropdown) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
                padding: "12px",
                borderRadius: "8px",
                backgroundColor: "#F8FAFC",
                border: "1px solid #E5E7EB",
                "&:hover": {
                  backgroundColor: "#F1F5F9",
                  borderColor: "#CBD5E1",
                },
              }}
            >
              {editField === "fuelType" ? (
                <Select
                  fullWidth
                  name="fuelType"
                  value={vehiculeInfo.fuelType || ""}
                  onChange={handleInputChange}
                  sx={{
                    backgroundColor: "#FAFBFC",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0085FF",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#0085FF",
                    },
                  }}
                >
                  {fuelOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography
                  sx={{
                    fontWeight: "600",
                    fontSize: "14px",
                    flex: 1,
                    color: "#374151",
                  }}
                >
                  Type de carburant:{" "}
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: "normal",
                      color: "#6B7280",
                    }}
                  >
                    {vehiculeInfo.fuelType || "N/A"}
                  </Typography>
                </Typography>
              )}
              <IconButton
                onClick={() =>
                  editField === "fuelType"
                    ? handleSave("fuelType")
                    : handleEdit("fuelType")
                }
                sx={{
                  color: "#0085FF",
                  "&:hover": {
                    backgroundColor: "#E3F2FD",
                  },
                }}
              >
                {editField === "fuelType" ? <SaveIcon /> : <EditIcon />}
              </IconButton>
            </Box>

            <Divider sx={{ margin: "24px 0", borderColor: "#E5E7EB" }} />

            {/* Validité Assurance (Date Picker) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              {editField === "insuranceValidity" ? (
                <>
                  <MobileDatePicker
                    value={
                      vehiculeInfo.insuranceValidity
                        ? dayjs(vehiculeInfo.insuranceValidity)
                        : null
                    }
                    onChange={(date) =>
                      handleDateChange("insuranceValidity", date)
                    }
                  />
                  <IconButton onClick={() => handleSave("insuranceValidity")}>
                    <SaveIcon />
                  </IconButton>
                </>
              ) : (
                <Typography sx={{ fontWeight: "bold", flex: 1 }}>
                  Date d'expiration de l'assurance:{" "}
                  <Typography component="span">
                    {vehiculeInfo.insuranceValidity
                      ? dayjs(vehiculeInfo.insuranceValidity).format(
                          "DD/MM/YYYY"
                        )
                      : "N/A"}
                  </Typography>
                </Typography>
              )}
              <IconButton onClick={() => handleEdit("insuranceValidity")}>
                <EditIcon />
              </IconButton>
            </Box>

            {/* Validité Contrôle Technique (Date Picker) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              {editField === "technicalControlValidity" ? (
                <>
                  <MobileDatePicker
                    value={
                      vehiculeInfo.technicalControlValidity
                        ? dayjs(vehiculeInfo.technicalControlValidity)
                        : null
                    }
                    onChange={(date) =>
                      handleDateChange("technicalControlValidity", date)
                    }
                  />
                  <IconButton
                    onClick={() => handleSave("technicalControlValidity")}
                  >
                    <SaveIcon />
                  </IconButton>
                </>
              ) : (
                <Typography sx={{ fontWeight: "bold", flex: 1 }}>
                  Date d'expiration du contrôle technique:{" "}
                  <Typography component="span">
                    {vehiculeInfo.technicalControlValidity
                      ? dayjs(vehiculeInfo.technicalControlValidity).format(
                          "DD/MM/YYYY"
                        )
                      : "N/A"}
                  </Typography>
                </Typography>
              )}
              <IconButton
                onClick={() => handleEdit("technicalControlValidity")}
              >
                <EditIcon />
              </IconButton>
            </Box>

            <Divider sx={{ margin: "24px 0", borderColor: "#E5E7EB" }} />

            {/* Rappel Véhicule */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                borderRadius: "8px",
                backgroundColor: "#F8FAFC",
                border: "1px solid #E5E7EB",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#374151",
                  }}
                >
                  Activer le rappel du véhicule
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#6B7280",
                    marginTop: "2px",
                  }}
                >
                  Recevez des rappels pour vos documents qui expirent
                </Typography>
              </Box>
              <Switch
                checked={vehicleReminder}
                onChange={handleVehicleReminderChange}
                disabled={updateIsLoading}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#0085FF",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#0085FF",
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default Vehicule;
